import React, { useEffect, useState } from 'react';
import { formatCurrencyWhole as fmt } from '../../lib/format';
import { Award, Crown, Gem, Sparkles, TrendingUp, Truck, Zap } from 'lucide-react';
import { apiClient } from '../../lib/api';

interface Tier {
  label: string;
  thresholdAmount: number;
  discountPercent: number;
  freeShipping: boolean;
  priorityFulfillment: boolean;
  perks: string[];
}

interface Status {
  enabled: boolean;
  programActive: boolean;
  tiers: Tier[];
  currentWeek: {
    start: string;
    end: string;
    daysRemaining: number;
    grossSubtotal: number;
    /** Issued-but-unpaid this week. Shown for context; does NOT earn tier. */
    pendingSubtotal: number;
    projectedTier: Tier | null;
    nextTier: { label: string; thresholdAmount: number; gap: number } | null;
  };
  activeTier: (Tier & { sourceWeekStart: string }) | null;
}

interface TierTheme {
  // Background gradient classes for the hero
  hero: string;
  // Accent ring/glow for the tier badge
  badgeRing: string;
  badgeBg: string;
  iconText: string;
  // Text color on hero
  textPrimary: string;
  textSecondary: string;
  // Perk chip styling
  chipBg: string;
  chipText: string;
  // Ladder dot color when this tier is reached
  ladderActive: string;
  // Color for checkmark text on reached ladder dots
  ladderCheckText: string;
  // Optional shimmer overlay
  shimmer: boolean;
  Icon: React.ComponentType<{ className?: string }>;
}

const TIER_THEMES: Record<string, TierTheme> = {
  None: {
    hero: 'bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50',
    badgeRing: 'ring-slate-300',
    badgeBg: 'bg-white',
    iconText: 'text-slate-500',
    textPrimary: 'text-slate-800',
    textSecondary: 'text-slate-600',
    chipBg: 'bg-white border border-slate-200',
    chipText: 'text-slate-700',
    ladderActive: 'bg-slate-400',
    ladderCheckText: 'text-white',
    shimmer: false,
    Icon: TrendingUp
  },
  Silver: {
    hero: 'bg-gradient-to-br from-slate-100 via-zinc-200 to-slate-300',
    badgeRing: 'ring-slate-400',
    badgeBg: 'bg-gradient-to-br from-white to-slate-200',
    iconText: 'text-slate-700',
    textPrimary: 'text-slate-900',
    textSecondary: 'text-slate-700',
    chipBg: 'bg-white/80 border border-slate-300',
    chipText: 'text-slate-800',
    ladderActive: 'bg-slate-500',
    ladderCheckText: 'text-white',
    shimmer: false,
    Icon: Award
  },
  Gold: {
    // Richer, more metallic gold — deeper saturation top→bottom and a warm
    // amber base so the card actually reads as "gold."
    hero: 'bg-gradient-to-br from-yellow-300 via-amber-400 to-yellow-600',
    badgeRing: 'ring-yellow-200',
    badgeBg: 'bg-gradient-to-br from-yellow-200 via-amber-300 to-yellow-500',
    iconText: 'text-amber-900',
    textPrimary: 'text-amber-950',
    textSecondary: 'text-amber-900',
    chipBg: 'bg-white/70 border border-amber-500',
    chipText: 'text-amber-950',
    ladderActive: 'bg-amber-600',
    ladderCheckText: 'text-white',
    shimmer: true,
    Icon: Crown
  },
  Platinum: {
    hero: 'bg-gradient-to-br from-slate-700 via-slate-800 to-indigo-900',
    badgeRing: 'ring-indigo-300',
    badgeBg: 'bg-gradient-to-br from-slate-200 to-indigo-100',
    iconText: 'text-indigo-700',
    textPrimary: 'text-white',
    textSecondary: 'text-indigo-100',
    chipBg: 'bg-white/15 backdrop-blur border border-white/20',
    chipText: 'text-white',
    ladderActive: 'bg-indigo-300',
    ladderCheckText: 'text-indigo-900',
    shimmer: true,
    Icon: Gem
  },
  Diamond: {
    hero: 'bg-gradient-to-br from-cyan-300 via-sky-500 to-violet-700',
    badgeRing: 'ring-cyan-200',
    badgeBg: 'bg-gradient-to-br from-cyan-100 to-violet-200',
    iconText: 'text-violet-700',
    textPrimary: 'text-white',
    textSecondary: 'text-cyan-50',
    chipBg: 'bg-white/15 backdrop-blur border border-white/30',
    chipText: 'text-white',
    ladderActive: 'bg-white',
    ladderCheckText: 'text-violet-700',
    shimmer: true,
    Icon: Sparkles
  }
};

const themeFor = (label: string | null | undefined): TierTheme => {
  return (label && TIER_THEMES[label]) || TIER_THEMES.None;
};

const VolumeRewardsWidget: React.FC = () => {
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await apiClient.getMyVolumeRewardsStatus();
      if (res.success && res.data) setStatus(res.data);
      setLoading(false);
    })();
  }, []);

  if (loading || !status || !status.enabled || !status.programActive) return null;

  const { activeTier, currentWeek } = status;
  const tiers = status.tiers || [];

  const theme = themeFor(activeTier?.label);
  const Icon = theme.Icon;

  const activeIdx = activeTier
    ? tiers.findIndex(t => t.label === activeTier.label)
    : -1;
  const projectedIdx = currentWeek.projectedTier
    ? tiers.findIndex(t => t.label === currentWeek.projectedTier!.label)
    : -1;

  const paid = currentWeek.grossSubtotal;
  const pending = currentWeek.pendingSubtotal || 0;

  // Measure against the NEXT threshold, not the top of the ladder — a clinic
  // $1,175 from Silver should read as "over halfway," not 7% of Diamond.
  // Once they're at the top tier there's nothing left to chase, so peg to it.
  const target = currentWeek.nextTier
    ? currentWeek.nextTier.thresholdAmount
    : (tiers.length ? tiers[tiers.length - 1].thresholdAmount : 0);
  const atTopTier = tiers.length > 0 && !currentWeek.nextTier && projectedIdx === tiers.length - 1;

  const paidPct = target > 0 ? Math.min(100, (paid / target) * 100) : 0;
  // Pending rides on top of paid and is clamped so the pair never exceeds 100%.
  const pendingPct = target > 0
    ? Math.max(0, Math.min(100 - paidPct, (pending / target) * 100))
    : 0;

  // Next week's tier vs. the one being redeemed right now.
  const willRise = projectedIdx > activeIdx;
  const willDrop = activeIdx >= 0 && projectedIdx < activeIdx;
  const gapToHold = activeTier ? Math.max(0, activeTier.thresholdAmount - paid) : 0;
  const urgent = willDrop && currentWeek.daysRemaining <= 2;

  const onWhite = !theme.textPrimary.includes('white');
  const weekLabel = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div className={`relative overflow-hidden rounded-2xl shadow-lg ${theme.hero} p-6 mb-6`}>
      {/* Shimmer sweep for premium tiers */}
      {theme.shimmer && (
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: 'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.18) 50%, transparent 70%)',
            backgroundSize: '200% 100%',
            animation: 'volumeRewardShimmer 6s ease-in-out infinite'
          }}
        />
      )}

      <style>{`
        @keyframes volumeRewardShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      <div className="relative flex items-start justify-between gap-6">
        {/* Tier identity */}
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 rounded-2xl ${theme.badgeBg} ring-2 ${theme.badgeRing} ring-offset-2 ring-offset-transparent flex items-center justify-center shadow-lg`}>
            <Icon className={`w-8 h-8 ${theme.iconText}`} />
          </div>
          <div>
            <div className={`text-xs uppercase tracking-widest font-semibold ${theme.textSecondary}`}>
              Redeeming this week
            </div>
            <div className={`text-3xl font-bold ${theme.textPrimary} leading-tight`}>
              {activeTier ? activeTier.label : 'No tier yet'}
            </div>
            <div className={`text-xs ${theme.textSecondary} mt-1`}>
              {activeTier
                ? `Earned on the week of ${weekLabel(activeTier.sourceWeekStart)}`
                : 'Hit a threshold this week to unlock rewards next week'}
            </div>
          </div>
        </div>

        {/* Perks rail */}
        {activeTier && (
          <div className="flex flex-wrap gap-1.5 max-w-md justify-end">
            {activeTier.discountPercent > 0 && (
              <span className={`inline-flex items-center gap-1 ${theme.chipBg} ${theme.chipText} text-xs font-semibold px-2.5 py-1 rounded-full`}>
                <span className="text-sm">{activeTier.discountPercent}%</span> off
              </span>
            )}
            {activeTier.freeShipping && (
              <span className={`inline-flex items-center gap-1 ${theme.chipBg} ${theme.chipText} text-xs font-semibold px-2.5 py-1 rounded-full`}>
                <Truck className="w-3 h-3" /> Free shipping
              </span>
            )}
            {activeTier.priorityFulfillment && (
              <span className={`inline-flex items-center gap-1 ${theme.chipBg} ${theme.chipText} text-xs font-semibold px-2.5 py-1 rounded-full`}>
                <Zap className="w-3 h-3" /> Priority
              </span>
            )}
            {(activeTier.perks || []).map(p => (
              <span key={p} className={`${theme.chipBg} ${theme.chipText} text-xs px-2.5 py-1 rounded-full`}>
                {p}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Status ladder */}
      {tiers.length > 0 && (
        <div className="relative mt-7 pt-1">
          {/* connector line */}
          <div className={`absolute left-0 right-0 top-[18px] h-px ${activeTier?.label === 'Platinum' || activeTier?.label === 'Diamond' ? 'bg-white/30' : 'bg-slate-300'}`} />
          <div
            className={`absolute left-0 top-[17px] h-[3px] rounded-full ${theme.ladderActive}`}
            style={{
              width: activeIdx >= 0
                ? `${(activeIdx / Math.max(1, tiers.length - 1)) * 100}%`
                : '0%'
            }}
          />

          <div className="relative flex justify-between">
            {tiers.map((t, idx) => {
              const reached = activeIdx >= idx;
              // Where this week's volume currently lands — shown in BOTH
              // directions so a clinic sliding backwards sees it, not just one
              // climbing.
              const tracking = projectedIdx === idx;
              return (
                <div key={t.label} className="flex flex-col items-center" style={{ flex: '0 0 auto', width: `${100 / tiers.length}%` }}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                    reached
                      ? `${theme.ladderActive} shadow-md`
                      : tracking
                        ? 'border-2 border-dashed ' + (onWhite ? 'bg-white border-slate-400' : 'bg-white/10 border-white/60')
                        : (onWhite ? 'bg-white border border-slate-300' : 'bg-white/20')
                  }`}>
                    {reached ? (
                      <span className={`text-xs font-bold ${theme.ladderCheckText}`}>
                        ✓
                      </span>
                    ) : (
                      <span className={`text-xs font-semibold ${theme.textSecondary}`}>
                        {idx + 1}
                      </span>
                    )}
                  </div>
                  <div className={`text-[11px] font-semibold mt-1.5 ${theme.textPrimary}`}>
                    {t.label}
                  </div>
                  <div className={`text-[10px] ${theme.textSecondary}`}>
                    {fmt(t.thresholdAmount)}
                  </div>
                  {tracking && (
                    <div className={`text-[9px] uppercase tracking-wide font-bold mt-0.5 ${theme.textPrimary}`}>
                      ▲ tracking
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Next week's earning progress — a distinct period from the hero above */}
      <div className={`relative mt-6 pt-5 border-t ${onWhite ? 'border-slate-300/60' : 'border-white/15'}`}>
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <div className={`text-xs uppercase tracking-wide font-semibold ${theme.textSecondary}`}>
              Earning for next week
            </div>
            <div className={`text-2xl font-bold ${theme.textPrimary}`}>
              {fmt(paid)}
              <span className={`ml-2 text-xs font-medium ${theme.textSecondary}`}>paid</span>
            </div>
            {pending > 0 && (
              <div className={`text-xs mt-0.5 ${theme.textSecondary}`}>
                + {fmt(pending)} invoiced, awaiting payment — counts once paid
              </div>
            )}
          </div>

          <div className="text-right">
            <div className={`text-xs uppercase tracking-wide font-semibold ${theme.textSecondary}`}>
              Week closes
            </div>
            <div className={`text-base font-semibold ${urgent ? 'text-red-600' : theme.textPrimary}`}>
              {currentWeek.daysRemaining <= 0
                ? 'Today'
                : `${currentWeek.daysRemaining} day${currentWeek.daysRemaining === 1 ? '' : 's'} left`}
            </div>
            <div className={`text-[10px] ${theme.textSecondary}`}>
              {weekLabel(currentWeek.start)} – {weekLabel(currentWeek.end)}
            </div>
          </div>
        </div>

        {/* Paid (solid) + pending (striped) against the NEXT threshold */}
        <div className={`mt-3 flex h-2 rounded-full overflow-hidden ${onWhite ? 'bg-slate-300/60' : 'bg-white/15'}`}>
          <div
            className={`h-full ${theme.ladderActive} transition-all duration-700`}
            style={{ width: `${paidPct}%` }}
          />
          {pendingPct > 0 && (
            <div
              className="h-full transition-all duration-700 opacity-60"
              style={{
                width: `${pendingPct}%`,
                backgroundImage:
                  'repeating-linear-gradient(45deg, currentColor 0 4px, transparent 4px 8px)',
                color: onWhite ? '#64748b' : 'rgba(255,255,255,0.9)'
              }}
            />
          )}
        </div>

        <div className={`mt-1.5 flex justify-between text-[10px] ${theme.textSecondary}`}>
          <span>{fmt(0)}</span>
          <span>
            {atTopTier
              ? 'Top tier'
              : `${Math.round(paidPct)}% to ${currentWeek.nextTier?.label ?? '—'} · ${fmt(target)}`}
          </span>
        </div>

        {/* What next week actually looks like right now */}
        <div className="mt-3 space-y-1">
          {willDrop ? (
            <div className={`text-sm font-semibold ${urgent ? 'text-red-600' : theme.textPrimary}`}>
              ⚠ Tracking below {activeTier!.label} — {fmt(gapToHold)} more to hold it
              {currentWeek.projectedTier
                ? `, otherwise you drop to ${currentWeek.projectedTier.label} next week`
                : ', otherwise you lose your tier next week'}
            </div>
          ) : currentWeek.projectedTier ? (
            <div className={`text-sm font-semibold ${theme.textPrimary}`}>
              {willRise ? '▲ On track to reach' : '✓ On track to hold'}{' '}
              {currentWeek.projectedTier.label} next week
            </div>
          ) : (
            <div className={`text-sm font-semibold ${theme.textPrimary}`}>
              No tier yet for next week
            </div>
          )}

          {currentWeek.nextTier && (
            <div className={`text-sm ${theme.textSecondary}`}>
              <span className={`font-semibold ${theme.textPrimary}`}>{fmt(currentWeek.nextTier.gap)}</span>{' '}
              more to reach <span className={`font-semibold ${theme.textPrimary}`}>{currentWeek.nextTier.label}</span>
            </div>
          )}
        </div>

        <div className={`mt-3 text-[10px] ${theme.textSecondary}`}>
          Counts invoices paid Mon–Sun (UTC). Tiers earned one week apply the next.
        </div>
      </div>
    </div>
  );
};

export default VolumeRewardsWidget;
